import { useState, useEffect, useCallback } from "react";
import "./MyAddress.css";
import {
  addressCreate,
  addressUpdate,
  addressDelete,
  getAddressList,
  addressDeliver,
  toastSuccess,
  toastError,
} from "../../api/services";
import useAuth from "../../Features/Utils/useAuth";
import { AiOutlineCheck } from "react-icons/ai";

const MyAddress = () => {
  const { user } = useAuth();
  const [showModal, setShowModal] = useState(false);
  const [editAddressId, setEditAddressId] = useState(null);
  const [deleteId, setDeleteId] = useState(null);
  const [deliverId, setDeliverId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const [formData, setFormData] = useState({
    label: "",
    street: "",
    city: "",
    state: "",
    country: "",
    zipCode: "",
    order_note: "",
  });

  const [addresses, setAddresses] = useState([]);
  const fetchAddresses = useCallback(async () => {
    if (!user?.id) return;
    setLoading(true);
    setError("");
    try {
      const response = await getAddressList({ userId: user.id });
      if (response?.data?.success) {
        setAddresses(response?.data?.data || []);
      } else {
        setError(response?.data?.message || "Failed to load addresses");
      }
    } catch (err) {
      console.error("Fetch addresses error:", err);
      setError("Network error. Please try again.");
    } finally {
      setLoading(false);
    }
  }, [user?.id]);

  useEffect(() => {
    fetchAddresses();
  }, [fetchAddresses]);

  useEffect(() => {
    document.body.style.overflow =
      showModal || deleteId || deliverId || loading ? "hidden" : "unset";
    return () => {
      document.body.style.overflow = "unset";
    };
  }, [showModal, deleteId, deliverId, loading]);

  const isLabelAvailable = (label, currentAddressId = null) => {
    if (label !== "Home" && label !== "Work") return true;
    return !addresses.some(
      (addr) => addr.label === label && addr.address_id !== currentAddressId,
    );
  };

  const isRadioDisabled = (label) => {
    if (editAddressId) {
      const current = addresses.find((a) => a.address_id === editAddressId);
      if (current && current.label === label) return false; // can keep same type
      return !isLabelAvailable(label, editAddressId);
    } else {
      return !isLabelAvailable(label, null);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    setError("");
  };

  const handleAdd = () => {
    setEditAddressId(null);
    setFormData({
      label: "",
      street: "",
      city: "",
      state: "",
      country: "",
      zipCode: "",
      order_note: "",
    });
    setError("");
    setShowModal(true);
  };

  const handleEdit = (item) => {
    setEditAddressId(item.address_id);
    setFormData({
      label: item.label,
      street: item.street || "",
      city: item.city || "",
      state: item.state || "",
      country: item.country || "",
      zipCode: item.zipCode || "",
      order_note: item.order_note || "",
    });
    setError("");
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.label) {
      setError("Please select an address type");
      return;
    }
    if (!isLabelAvailable(formData.label, editAddressId)) {
      setError(
        `You already have a "${formData.label}" address. Only one allowed.`,
      );
      return;
    }

    setLoading(true);
    setError("");
    try {
      if (editAddressId) {
        await addressUpdate({
          id: editAddressId,
          label: formData.label,
          street: formData.street,
          city: formData.city,
          state: formData.state,
          country: formData.country,
          zipCode: formData.zipCode,
          order_note: formData.order_note,
        });
        toastSuccess("Address updated successfully");
      } else {
        await addressCreate({
          userId: user.id,
          label: formData.label,
          street: formData.street,
          city: formData.city,
          state: formData.state,
          country: formData.country,
          zipCode: formData.zipCode,
          order_note: formData.order_note,
        });
        toastSuccess("Address added successfully");
      }

      await fetchAddresses();
      setShowModal(false);
    } catch (err) {
      console.error("Save error:", err);
      toastError(err.response?.data?.message || "Failed to save address");
      setError(err.response?.data?.message || "Failed to save address");
    } finally {
      setLoading(false);
    }
  };

  const handleDeliver = async () => {
    if (!deliverId) return;
    setLoading(true);
    setError("");
    try {
      await addressDeliver(user.id, deliverId);
      // Optionally show success message or just refresh
      toastSuccess("Address Delivered successfully");
      await fetchAddresses();
      setDeliverId(null);
    } catch (err) {
      console.error("Deliver error:", err);
      toastError("Failed to set default address");
      setError("Failed to set default address");
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteId) return;
    setLoading(true);
    setError("");
    try {
      await addressDelete({ id: deleteId });
      toastSuccess("Address Deleted successfully");
      await fetchAddresses();
      setDeleteId(null);
    } catch (err) {
      console.error("Delete error:", err);
      toastError("Failed to delete address");
      setError("Failed to delete address");
    } finally {
      setLoading(false);
    }
  };

  const formatFullAddress = (item) => {
    return `${item.street}, ${item.city}, ${item.state} ${item.zipCode}, ${item.country}`;
  };

  return (
    <div className="my_acc_page main__wrapper">
      <div className="my_acc_page_content content__page section-padding">
        <div className="container">
          <div className="row">
            <div className="col-md-12">
              <div className="row">
                <div className="col-12 col-md-12 col-xl-12">
                  <div className="step-checkout-div">
                    <div className="steps activestep">
                      <div className="icondiv">
                        <i className="fas fa-users"></i>
                      </div>
                      <h4 className="step-ttl">Manage address</h4>
                      <div className="before-login">
                        <p className="desq">Select Address Or Add Address</p>

                        {error && !showModal && !deleteId && !deliverId && (
                          <div className="alert alert-danger">{error}</div>
                        )}

                        <div className="addresslist-div">
                          {addresses.map((item) => (
                            <div className="addresslist" key={item.address_id}>
                              <div
                                style={{
                                  position: "relative",
                                  display: "flex",
                                  justifyContent: "space-between",
                                }}
                              >
                                <h4>{item.label}</h4>
                                {item?.isDefault === 1 && (
                                  <div
                                    style={{
                                      width: "24px",
                                      height: "25px",
                                      display: "flex",
                                      alignItems: "center",
                                      justifyContent: "center",
                                      background: "#0a0f0f",
                                      borderRadius: "50%",
                                    }}
                                  >
                                    <AiOutlineCheck
                                      style={{
                                        color: "#ffffff",
                                        fontSize: "14px",
                                      }}
                                    />
                                  </div>
                                )}
                              </div>

                              <p>
                                {formatFullAddress(item)}
                                <br />
                                {item.order_note && (
                                  <>
                                    Note: {item.order_note}
                                    <br />
                                  </>
                                )}
                              </p>
                              <div className="address-actions">
                                <button
                                  type="button"
                                  className="btn"
                                  onClick={() => setDeliverId(item.address_id)}
                                >
                                  Deliver Here
                                </button>
                                <button
                                  type="button"
                                  className="btn"
                                  onClick={() => handleEdit(item)}
                                >
                                  Edit
                                </button>
                                <button
                                  type="button"
                                  className="btn"
                                  onClick={() => setDeleteId(item.address_id)}
                                >
                                  Delete
                                </button>
                              </div>
                            </div>
                          ))}

                          <div className="addresslist add-new-card">
                            <h4>Add New Address</h4>
                            <button
                              type="button"
                              className="btn"
                              onClick={handleAdd}
                            >
                              Add New
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ADD/EDIT MODAL */}
      {showModal && (
        <div className="modal-overlay">
          <div className="modal-container">
            <div className="modal-content-custom">
              <div className="modal-header-custom">
                <h4>{editAddressId ? "Edit Address" : "Add New Address"}</h4>
                <button
                  type="button"
                  className="close-btn"
                  onClick={() => setShowModal(false)}
                  disabled={loading}
                >
                  ×
                </button>
              </div>
              <div className="modal-body-custom">
                <form onSubmit={handleSubmit} id="addressForm">
                  <div className="row">
                    <div className="mb-4 col-lg-12">
                      <label className="form-label d-block mb-2">
                        Address Type *
                      </label>
                      <div className="d-flex gap-4">
                        {["Home", "Work", "Other"].map((type) => (
                          <label key={type} className="radio-label">
                            <input
                              type="radio"
                              name="label"
                              value={type}
                              checked={formData.label === type}
                              onChange={handleChange}
                              disabled={loading || isRadioDisabled(type)}
                            />
                            <span className="ms-2">{type}</span>
                          </label>
                        ))}
                      </div>
                      <small className="text-muted">
                        {!editAddressId &&
                          (addresses.some((a) => a.label === "Home")
                            ? "Home already exists"
                            : "")}
                        {!editAddressId &&
                          (addresses.some((a) => a.label === "Work")
                            ? "Work already exists"
                            : "")}
                      </small>
                    </div>

                    <div className="mb-4 col-lg-12">
                      <textarea
                        name="street"
                        className="form-control"
                        rows="4"
                        placeholder="Street Address *"
                        value={formData.street}
                        onChange={handleChange}
                        required
                        disabled={loading}
                      />
                    </div>
                    <div className="mb-4 col-lg-4">
                      <input
                        type="text"
                        name="city"
                        className="form-control"
                        placeholder="City *"
                        value={formData.city}
                        onChange={handleChange}
                        required
                        disabled={loading}
                      />
                    </div>
                    <div className="mb-4 col-lg-4">
                      <input
                        type="text"
                        name="state"
                        className="form-control"
                        placeholder="State *"
                        value={formData.state}
                        onChange={handleChange}
                        required
                        disabled={loading}
                      />
                    </div>
                    <div className="mb-4 col-lg-4">
                      <input
                        type="text"
                        name="zipCode"
                        className="form-control"
                        placeholder="Zip Code *"
                        value={formData.zipCode}
                        onChange={handleChange}
                        required
                        disabled={loading}
                      />
                    </div>
                    <div className="mb-4 col-lg-12">
                      <input
                        type="text"
                        name="country"
                        className="form-control"
                        placeholder="Country *"
                        value={formData.country}
                        onChange={handleChange}
                        required
                        disabled={loading}
                      />
                    </div>
                    <div className="mb-4 col-lg-12">
                      <textarea
                        name="order_note"
                        className="form-control"
                        rows="4"
                        placeholder="Order Note / Remark (optional)"
                        value={formData.order_note}
                        onChange={handleChange}
                        disabled={loading}
                      />
                    </div>
                  </div>
                </form>
              </div>
              <div className="modal-footer">
                <button
                  type="submit"
                  form="addressForm"
                  className="btn btn-submit-model"
                  disabled={loading}
                >
                  {loading ? (
                    <span className="spinner"></span>
                  ) : editAddressId ? (
                    "Update"
                  ) : (
                    "Add"
                  )}
                </button>
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setShowModal(false)}
                  disabled={loading}
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* DELIVER (SET DEFAULT) MODAL */}
      {deliverId && (
        <div className="modal-overlay">
          <div className="modal-container modal-sm">
            <div className="modal-content-custom">
              <div className="modal-header-custom">
                <h4>Set as Default Address</h4>
                <button
                  type="button"
                  className="close-btn"
                  onClick={() => setDeliverId(null)}
                  disabled={loading}
                >
                  ×
                </button>
              </div>
              <div className="modal-body-custom">
                <p className="deleteAddressP">
                  Are you sure you want to set this address as default for
                  delivery?
                </p>
                {error && (
                  <div className="alert alert-danger mt-2">{error}</div>
                )}
              </div>
              <div className="modal-footer">
                <button
                  className="btn btn-danger"
                  onClick={handleDeliver}
                  disabled={loading}
                >
                  {loading ? (
                    <span className="spinner"></span>
                  ) : (
                    "Yes, Set Default"
                  )}
                </button>
                <button
                  className="btn btn-secondary"
                  onClick={() => setDeliverId(null)}
                  disabled={loading}
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* DELETE MODAL */}
      {deleteId && (
        <div className="modal-overlay">
          <div className="modal-container modal-sm">
            <div className="modal-content-custom">
              <div className="modal-header-custom">
                <h4>Delete Address</h4>
                <button
                  type="button"
                  className="close-btn"
                  onClick={() => setDeleteId(null)}
                  disabled={loading}
                >
                  ×
                </button>
              </div>
              <div className="modal-body-custom">
                <p className="deleteAddressP">
                  Are you sure you want to delete this address?
                </p>
                {error && (
                  <div className="alert alert-danger mt-2">{error}</div>
                )}
              </div>
              <div className="modal-footer">
                <button
                  className="btn btn-danger"
                  onClick={handleDelete}
                  disabled={loading}
                >
                  {loading ? <span className="spinner"></span> : "Yes Delete"}
                </button>
                <button
                  className="btn btn-secondary"
                  onClick={() => setDeleteId(null)}
                  disabled={loading}
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MyAddress;
